import { supabase } from './supabaseClient';
import { serviceRoleClient } from './serviceRoleClient';

export class TcCouponService {
  /** Admin: per-telecaller RSA coupon codes + use stats */
  static async listAdminStats() {
    const { data, error } = await supabase.rpc('admin_list_tc_rsa_coupon_stats');
    if (error) throw error;
    const parsed = data || {};
    return Array.isArray(parsed.telecallers) ? parsed.telecallers : [];
  }

  /** Admin: current TC universal coupon discount % */
  static async getUniversalDiscountPercent() {
    const { data, error } = await supabase.rpc('admin_get_tc_rsa_universal_discount');
    if (error) throw error;
    const parsed = data || {};
    const pct = Number(parsed.discount_percent);
    return Number.isFinite(pct) && pct > 0 ? pct : 50;
  }

  /**
   * Admin: set TC universal coupon discount % (updates all active TC universal codes).
   * @param {number} percent
   */
  static async setUniversalDiscountPercent(percent) {
    const { data, error } = await supabase.rpc('admin_set_tc_rsa_universal_discount', {
      p_percent: Number(percent),
    });
    if (error) throw error;
    const parsed = data || {};
    if (!parsed.success) throw new Error(parsed.error || 'Could not update discount');
    return parsed;
  }

  static async listCoupons(telecallerId) {
    const { data, error } = await supabase.rpc('telecaller_list_rsa_coupons', {
      p_telecaller_id: telecallerId,
    });
    if (error) throw error;
    const parsed = data || {};
    return Array.isArray(parsed.coupons) ? parsed.coupons : [];
  }

  static async listCouponUses(telecallerId, couponId) {
    const { data, error } = await supabase.rpc('telecaller_list_coupon_uses', {
      p_telecaller_id: telecallerId,
      p_coupon_id: couponId,
    });
    if (error) throw error;
    return data || {};
  }

  /** Promo / marketing coupons (not linked to a telecaller) */
  static async listPromoCoupons() {
    const { data, error } = await serviceRoleClient
      .from('coupons')
      .select(
        'id, code, discount_type, discount_value, applies_to_plan_slug, max_uses, uses_count, valid_until, is_active, created_at',
      )
      .is('telecaller_id', null)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  }

  static async listMembershipPlans() {
    const { data, error } = await serviceRoleClient
      .from('membership_plans')
      .select('slug, name, price, is_active')
      .eq('is_active', true)
      .order('position', { ascending: true });
    if (error) throw error;
    return data || [];
  }

  /**
   * Create a time-based promo coupon for admin campaigns.
   * @param {{ code: string, discount_type: 'fixed'|'percent', discount_value: number, applies_to_plan_slug?: string|null, max_uses?: number|null, valid_until?: string|null, is_active?: boolean }}
   */
  static async createPromoCoupon(payload) {
    const code = String(payload.code || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '');
    if (!code) throw new Error('Coupon code is required');
    if (!/^[A-Z0-9_-]{3,32}$/.test(code)) {
      throw new Error('Code must be 3–32 chars (letters, numbers, _ or -)');
    }

    const discountType = payload.discount_type === 'percent' ? 'percent' : 'fixed';
    const discountValue = Number(payload.discount_value);
    if (!Number.isFinite(discountValue) || discountValue <= 0) {
      throw new Error('Discount value must be greater than 0');
    }
    if (discountType === 'percent' && discountValue > 100) {
      throw new Error('Percent discount cannot exceed 100');
    }

    const planSlug = payload.applies_to_plan_slug
      ? String(payload.applies_to_plan_slug).trim()
      : null;

    let maxUses = null;
    if (payload.max_uses !== '' && payload.max_uses != null) {
      maxUses = Number(payload.max_uses);
      if (!Number.isFinite(maxUses) || maxUses < 1) {
        throw new Error('Max uses must be a positive number (or empty for unlimited)');
      }
    }

    let validUntil = null;
    if (payload.valid_until) {
      const d = new Date(payload.valid_until);
      if (Number.isNaN(d.getTime())) throw new Error('Invalid valid-until date');
      if (d.getTime() <= Date.now()) throw new Error('Valid until must be in the future');
      validUntil = d.toISOString();
    }

    const row = {
      code,
      discount_type: discountType,
      discount_value: discountValue,
      applies_to_plan_slug: planSlug || null,
      telecaller_id: null,
      max_uses: maxUses,
      uses_count: 0,
      valid_until: validUntil,
      is_active: payload.is_active !== false,
    };

    const { data, error } = await serviceRoleClient
      .from('coupons')
      .insert(row)
      .select(
        'id, code, discount_type, discount_value, applies_to_plan_slug, max_uses, uses_count, valid_until, is_active, created_at',
      )
      .single();

    if (error) {
      if (error.code === '23505') throw new Error(`Coupon code "${code}" already exists`);
      throw error;
    }
    return data;
  }

  /**
   * Update an existing promo coupon (code, discount, plan, expiry, max uses, active).
   * @param {string} couponId
   * @param {{ code: string, discount_type: 'fixed'|'percent', discount_value: number, applies_to_plan_slug?: string|null, max_uses?: number|null, valid_until?: string|null, is_active?: boolean }}
   */
  static async updatePromoCoupon(couponId, payload) {
    if (!couponId) throw new Error('Coupon id is required');

    const code = String(payload.code || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '');
    if (!code) throw new Error('Coupon code is required');
    if (!/^[A-Z0-9_-]{3,32}$/.test(code)) {
      throw new Error('Code must be 3–32 chars (letters, numbers, _ or -)');
    }

    const discountType = payload.discount_type === 'percent' ? 'percent' : 'fixed';
    const discountValue = Number(payload.discount_value);
    if (!Number.isFinite(discountValue) || discountValue <= 0) {
      throw new Error('Discount value must be greater than 0');
    }
    if (discountType === 'percent' && discountValue > 100) {
      throw new Error('Percent discount cannot exceed 100');
    }

    const planSlug = payload.applies_to_plan_slug
      ? String(payload.applies_to_plan_slug).trim()
      : null;

    let maxUses = null;
    if (payload.max_uses !== '' && payload.max_uses != null) {
      maxUses = Number(payload.max_uses);
      if (!Number.isFinite(maxUses) || maxUses < 1) {
        throw new Error('Max uses must be a positive number (or empty for unlimited)');
      }
    }

    let validUntil = null;
    if (payload.valid_until) {
      const d = new Date(payload.valid_until);
      if (Number.isNaN(d.getTime())) throw new Error('Invalid valid-until date');
      validUntil = d.toISOString();
    }

    const patch = {
      code,
      discount_type: discountType,
      discount_value: discountValue,
      applies_to_plan_slug: planSlug || null,
      max_uses: maxUses,
      valid_until: validUntil,
      is_active: payload.is_active !== false,
    };

    const { data, error } = await serviceRoleClient
      .from('coupons')
      .update(patch)
      .eq('id', couponId)
      .is('telecaller_id', null)
      .select(
        'id, code, discount_type, discount_value, applies_to_plan_slug, max_uses, uses_count, valid_until, is_active, created_at',
      )
      .single();

    if (error) {
      if (error.code === '23505') throw new Error(`Coupon code "${code}" already exists`);
      throw error;
    }
    return data;
  }

  static async setPromoCouponActive(couponId, isActive) {
    const { data, error } = await serviceRoleClient
      .from('coupons')
      .update({ is_active: !!isActive })
      .eq('id', couponId)
      .is('telecaller_id', null)
      .select('id, is_active')
      .single();
    if (error) throw error;
    return data;
  }
}
